import React, { useState, useReducer, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { Box, Divider, Typography, CircularProgress } from "@mui/material";
import ListIcon from '@mui/icons-material/List';
import { StandardPage, StandardPageTopContent, StandardPageMainContent, MuiCol, MuiRow, MuiBox } from "../../../components/layout";
import { useAppContext } from "../../../components/context/appContext";
import { ScannerInput, MuiTextField, MuiDate, MuiSelect } from '../../../components/inputs';
import { MuiButton, MuiStepper } from "../../../components/mui";
import palletApi from "../../../api/palletApi";
import shipmentApi from "../../../api/shipmentApi";
import netsuiteApi from "../../../api/netsuiteApi";
import masterpackApi from "../../../api/masterpackApi";
import { expeditionType } from "../../../actions/staticDataList";
import plantApi from "../../../api/plantApi";
import findPoNumbers from "../../../utils/poNumberLoader";
import cameraModelApi from "../../../api/cameraModelApi";
import ShipmentListDialog from "./forms/shipmentSelection";


// ---------------------------------------------------------------------------
// PO IDENTIFIERS — two distinct values, never interchangeable:
//
//   poId     — NetSuite internal ID, e.g. "10132368". Persisted, sent to
//              NetSuite, and used as the key for every lookup and grouping.
//              The cameras/items API returns it under a field it calls
//              `poNumber`; that upstream name is a misnomer.
//
//   poNumber — human-readable PO number, e.g. "PO2211002245". Display only.
//              Resolved by poNumberLoader and left blank ("") when it cannot
//              be resolved — never back-filled with poId, because the result
//              is persisted by buildPayload().
//
// Falling back to poId for *display* is fine and happens at render time
// (`poNumber || poId`). Falling back to poId in the *data* is not.
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Cost splitting
// ---------------------------------------------------------------------------

/**
 * Splits `total` proportionally across `entries` ({ id, quantity }) and
 * rounds to the cent.
 *
 * Rounding every entry's share independently (Math.round on each) loses or
 * gains a fraction of a cent per entry; summed back up for display, that
 * drifts away from the number the user typed — e.g. entering 5000 across a
 * handful of pallets redisplays as 4999.58. This instead rounds every share
 * DOWN to the cent, then hands the leftover cents to the entry with the
 * largest quantity. That keeps the sum of the returned shares exactly equal
 * to `total` (to the cent) in every case.
 *
 * Returns a Map<id, amountInDollars>. Empty if there is nothing to split
 * against (no entries, or total quantity of 0).
 */
function splitWithRemainderToLargest(entries, total) {
  const totalQuantity = entries.reduce((sum, e) => sum + e.quantity, 0);
  const result = new Map();
  if (totalQuantity <= 0 || entries.length === 0) return result;

  const totalCents = Math.round(total * 100);
  let allocatedCents = 0;
  let largestEntry = entries[0];

  for (const entry of entries) {
    const shareCents = Math.floor((entry.quantity / totalQuantity) * totalCents);
    result.set(entry.id, shareCents);
    allocatedCents += shareCents;
    if (entry.quantity > largestEntry.quantity) largestEntry = entry;
  }

  const remainder = totalCents - allocatedCents;
  result.set(largestEntry.id, result.get(largestEntry.id) + remainder);

  // Convert every share back from cents to dollars.
  for (const [id, cents] of result) result.set(id, cents / 100);
  return result;
}


// ---------------------------------------------------------------------------
// Reducers
// ---------------------------------------------------------------------------

/**
 * Writes `value` into ciNumber for every cameraModel entry matching
 * poId + itemSku, across every container (pallet or masterpack) in `state`.
 *
 * Used by the PO-first view: editing the CI Number for an aggregated
 * PO + SKU line updates ciNumber on all the underlying containers that
 * contribute to that line.
 */
function applyCiToContainers(state, poId, itemSku, value) {
  return state.map(container => ({
    ...container,
    pos: container.pos.map(po =>
      po.poId !== poId ? po : {
        ...po,
        cameraModel: po.cameraModel.map(model =>
          model.itemSku !== itemSku ? model : { ...model, ciNumber: value }
        )
      }
    )
  }));
}

/**
 * Writes the exact, already-split douane/transport amount for one
 * container's PO + SKU line. `amountsById` is keyed by the container's own
 * identifier (palletNumber or masterpackNumber, passed in as `idField`) and
 * holds the pre-computed { douane, transport } values from
 * splitWithRemainderToLargest — computed once, across pallets, masterpacks
 * and loose items together, so the parts sum exactly back to the total the
 * user entered. See handleCostCommit.
 */
function applyExactCostsToContainers(state, poId, itemSku, amountsById, idField) {
  return state.map(container => ({
    ...container,
    pos: container.pos.map(po =>
      po.poId !== poId ? po : {
        ...po,
        cameraModel: po.cameraModel.map(model => {
          if (model.itemSku !== itemSku) return model;
          const amounts = amountsById.get(container[idField]);
          if (!amounts) return model;
          return {
            ...model,
            douaneLineCost: amounts.douane,
            transportLineCost: amounts.transport,
          };
        })
      }
    )
  }));
}

/**
 * Same as applyCiToContainers, for the looseItems array — which has no
 * container wrapper, just { poId, poNumber, lines: [...] } entries directly.
 * Needed because a loose-only row has no pallet/masterpack for
 * applyCiToContainers to match against, so its CI field would otherwise never
 * write anywhere and the input would appear uneditable.
 */
function applyCiToLooseItems(looseItems, poId, itemSku, value) {
  return looseItems.map(entry =>
    entry.poId !== poId ? entry : {
      ...entry,
      lines: entry.lines.map(l =>
        l.itemSku !== itemSku ? l : { ...l, ciNumber: value }
      )
    }
  );
}

/**
 * Same as applyExactCostsToContainers, for looseItems. amountsById is keyed
 * by entry.poId here, since a loose entry is one row per PO with no
 * per-container identifier of its own.
 */
function applyExactCostsToLooseItems(looseItems, poId, itemSku, amountsById) {
  return looseItems.map(entry =>
    entry.poId !== poId ? entry : {
      ...entry,
      lines: entry.lines.map(l => {
        if (l.itemSku !== itemSku) return l;
        const amounts = amountsById.get(entry.poId);
        if (!amounts) return l;
        return {
          ...l,
          douaneLineCost: amounts.douane,
          transportLineCost: amounts.transport,
        };
      })
    }
  );
}

function palletsReducer(state, action) {
  switch (action.type) {
    case "ADD_PALLET":
      return [...state, action.payload];
    case "SET_CI_FOR_PO_SKU":
      return applyCiToContainers(state, action.poId, action.itemSku, action.value);
    case "SET_EXACT_COSTS_FOR_PO_SKU":
      return applyExactCostsToContainers(state, action.poId, action.itemSku, action.amountsById, 'palletNumber');
    case "REMOVE_PALLET":
      return state.filter(pallet => pallet.palletNumber !== action.palletNumber);
    case "RESET_PALLETS":
      return action.payload;
    case "UPDATE_PO_NUMBERS":
      return state.map(pallet =>
        pallet.palletNumber !== action.palletNumber ? pallet : {
          ...pallet,
          pos: pallet.pos.map(po => ({
            ...po,
            // Unresolved stays blank. Display falls back to poId at render
            // time; the stored value must never be the internal ID.
            poNumber: action.poNumberMap[po.poId] || po.poNumber || ""
          }))
        }
      );
    default:
      return state;
  }
}

function masterpacksReducer(state, action) {
  switch (action.type) {
    case "ADD_MASTERPACK":
      return [...state, action.payload];
    case "SET_CI_FOR_PO_SKU":
      return applyCiToContainers(state, action.poId, action.itemSku, action.value);
    case "SET_EXACT_COSTS_FOR_PO_SKU":
      return applyExactCostsToContainers(state, action.poId, action.itemSku, action.amountsById, 'masterpackNumber');
    case "REMOVE_MASTERPACK":
      return state.filter(mp => mp.masterpackNumber !== action.masterpackNumber);
    case "RESET_MASTERPACKS":
      return action.payload;
    case "UPDATE_PO_NUMBERS":
      return state.map(mp =>
        mp.masterpackNumber !== action.masterpackNumber ? mp : {
          ...mp,
          pos: mp.pos.map(po => ({
            ...po,
            // See the note in palletsReducer.
            poNumber: action.poNumberMap[po.poId] || po.poNumber || ""
          }))
        }
      );
    default:
      return state;
  }
}


// ---------------------------------------------------------------------------
// Data transforms
// ---------------------------------------------------------------------------

function transformPalletData(apiResponse) {
  const poMap = {};
  const nameBySku = {};

  for (const camera of apiResponse.cameras) {
    // camera.poNumber holds the PO internal ID despite its name — see the
    // PO IDENTIFIERS note at the top of this file.
    const poId = camera.poNumber;
    const masterPack = apiResponse.masterPacks.find(mp => mp.id === camera.masterPackId);
    const productCode = masterPack?.cameraModel?.productCode ?? null;
    const modelName = masterPack?.cameraModel?.name ?? null;

    if (modelName) nameBySku[productCode] = modelName;

    if (!poMap[poId]) poMap[poId] = {};
    if (!poMap[poId][productCode]) poMap[poId][productCode] = 0;
    poMap[poId][productCode]++;
  }

  const pos = Object.entries(poMap).map(([poId, skuMap]) => ({
    poId,
    poNumber: "",
    cameraModel: Object.entries(skuMap).map(([itemSku, quantity]) => ({
      itemNetsuiteId: null,
      itemSku,
      modelName: nameBySku[itemSku] ?? null,
      quantity,
      ciNumber: "",
    })),
  }));

  return {
    palletNumber: apiResponse.barcode,
    status: "standby",
    pos,
  };
}

function transformMasterpackData(apiResponse) {
  const poMap = {};
  // A masterpack is one model per scan, so its name sits on the masterpack's
  // own cameraModel object rather than per line item — item.cameraModel below
  // is the bare SKU string, not this object.
  const modelName = apiResponse?.cameraModel?.name ?? null;

  for (const item of apiResponse.items) {
    // item.poNumber holds the PO internal ID despite its name — see above.
    const poId = item.poNumber;
    const sku = item.cameraModel;
    if (!poMap[poId]) poMap[poId] = {};
    if (!poMap[poId][sku]) poMap[poId][sku] = 0;
    poMap[poId][sku]++;
  }

  const pos = Object.entries(poMap).map(([poId, skuMap]) => ({
    poId,
    poNumber: "",
    cameraModel: Object.entries(skuMap).map(([itemSku, quantity]) => ({
      itemNetsuiteId: null,
      itemSku,
      modelName,
      quantity,
      ciNumber: "",
    })),
  }));

  return {
    masterpackNumber: apiResponse.barcode,
    status: "standby",
    pos,
  };
}

/**
 * Resolves poId → poNumber for all pos entries in a list of pallets/masterpacks.
 * Updates poNumber in place for display only — does not persist back.
 *
 * A poId the loader cannot resolve leaves poNumber as-is (usually ""). It is
 * never back-filled with poId; see the PO IDENTIFIERS note at the top.
 */
async function enrichWithPoNumbers(items) {
  const allPoIds = items.flatMap(item =>
    (item.pos || []).map(po => po.poId).filter(Boolean)
  );

  if (allPoIds.length === 0) return items;

  const poNumberMap = await findPoNumbers([...new Set(allPoIds)]);

  return items.map(item => ({
    ...item,
    pos: (item.pos || []).map(po => ({
      ...po,
      poNumber: poNumberMap[po.poId] || po.poNumber || ""
    }))
  }));
}


// ---------------------------------------------------------------------------
// PO-first aggregation
//
// See "MSE Shipment UI — New Display Format" — Front-End: Load / Display
// Logic. Each pallet/masterpack still carries its own cameraModel entries
// with quantity, ciNumber, douaneLineCost and transportLineCost. This view
// is derived from that data on every render; nothing aggregated is stored.
// ---------------------------------------------------------------------------

/**
 * Builds one row per (poId, itemSku), combining pallets, masterpacks and
 * loose items.
 *
 * Lonely masterpacks contribute cameras but never count toward palletCount.
 * Loose items behave the same way: they have no container at all, so they
 * contribute quantity and costs to the line but not to palletCount.
 */
function buildViewMap(pallets, masterpacks, looseItems, nameBySku = {}) {
  const viewMap = {};

  for (const container of [...pallets, ...masterpacks]) {
    const isPallet = container.palletNumber !== undefined;

    for (const po of container.pos) {
      for (const cam of po.cameraModel) {
        const key = `${po.poId}__${cam.itemSku}`;

        if (!viewMap[key]) {
          viewMap[key] = {
            key,
            poId: po.poId,
            poNumber: po.poNumber,
            sku: cam.itemSku,
            // Older saved shipments never had modelName written to their
            // stored cameraModel entries — fall back to a lookup by SKU so
            // those still display a name instead of staying blank forever.
            modelName: cam.modelName ?? nameBySku[cam.itemSku] ?? null,
            netsuiteId: cam.itemNetsuiteId,
            ciNumber: cam.ciNumber,
            ciMismatch: false,
            douaneTotal: 0,
            transportTotal: 0,
            palletCount: 0,
            totalCameras: 0,
          };
        }

        const line = viewMap[key];
        line.totalCameras += cam.quantity;
        line.douaneTotal += cam.douaneLineCost ?? 0;
        line.transportTotal += cam.transportLineCost ?? 0;
        if (isPallet) line.palletCount += 1;
        if (cam.ciNumber !== line.ciNumber) line.ciMismatch = true;

        // Containers are enriched independently and asynchronously, so the
        // first one to define this line may still have a blank poNumber
        // while a later one has it resolved. Take the first non-blank.
        if (!line.poNumber && po.poNumber) line.poNumber = po.poNumber;
      }
    }
  }

  // Loose items are PO-level, not container-level: { poId, poNumber, lines[] }
  // rather than a container with pos[]. Each line lands on the same
  // poId__itemSku row a container line would, so a SKU that arrives both
  // loose and on a pallet shows as one row with the combined quantity.
  for (const entry of (looseItems || [])) {
    for (const line of (entry.lines || [])) {
      const key = `${entry.poId}__${line.itemSku}`;

      if (!viewMap[key]) {
        viewMap[key] = {
          key,
          poId: entry.poId,
          poNumber: entry.poNumber,
          sku: line.itemSku,
          modelName: nameBySku[line.itemSku] ?? null,
          netsuiteId: line.itemNetsuiteId,
          ciNumber: line.ciNumber,
          ciMismatch: false,
          douaneTotal: 0,
          transportTotal: 0,
          palletCount: 0,
          totalCameras: 0,
        };
      }

      const viewLine = viewMap[key];
      viewLine.totalCameras += line.quantity ?? 0;
      viewLine.douaneTotal += line.douaneLineCost ?? 0;
      viewLine.transportTotal += line.transportLineCost ?? 0;
      if (line.ciNumber !== viewLine.ciNumber) viewLine.ciMismatch = true;

      if (!viewLine.poNumber && entry.poNumber) viewLine.poNumber = entry.poNumber;
    }
  }

  // Reconstructed totals are the sum of per-container shares — round to
  // avoid floating point noise when re-displaying them. With
  // splitWithRemainderToLargest computing exact-cent shares up front, this
  // is now just defensive float cleanup, not compensating for drift.
  for (const line of Object.values(viewMap)) {
    line.douaneTotal = Math.round(line.douaneTotal * 100) / 100;
    line.transportTotal = Math.round(line.transportTotal * 100) / 100;
  }

  return viewMap;
}

/**
 * Groups the flat view map into one section per PO, with a stable
 * (PO number, then SKU) display order.
 */
function groupViewMapByPo(viewMap) {
  const groups = {};

  for (const line of Object.values(viewMap)) {
    if (!groups[line.poId]) {
      groups[line.poId] = { poId: line.poId, poNumber: line.poNumber, lines: [] };
    }
    // Display-only fallback: prefer any resolved poNumber for the header.
    if (!groups[line.poId].poNumber && line.poNumber) {
      groups[line.poId].poNumber = line.poNumber;
    }
    groups[line.poId].lines.push(line);
  }

  const result = Object.values(groups);
  result.forEach(group => group.lines.sort((a, b) => a.sku.localeCompare(b.sku)));
  result.sort((a, b) =>
    (a.poNumber || a.poId).localeCompare(b.poNumber || b.poId, undefined, { numeric: true })
  );

  return result;
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ShipmentSection({ shipment, onChange, expeditionTypeOptions, originCmOptions, destinationOptions, subCategoryOptions, editable, t }) {
  return (
    <MuiBox sx={{ mb: 3 }}>
      <MuiRow spacing={1}>
        <MuiCol size={{ xs: 12, md: 4 }}>
          <MuiTextField disabled value={shipment.shipmentCode} label={t("shipping_cm.shipment_id")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 2 }}>
          <MuiTextField disabled value={shipment.status} label={t("shipping_cm.status")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 3 }}>
          <MuiSelect
            disabled={!editable}
            options={originCmOptions}
            placeholder={t("shipping_cm.origin_cm")}
            value={originCmOptions.find(opt => opt.value === shipment.origineLocationId) || null}
            onChange={opt => onChange("origineLocationId", opt?.value ?? null)}
          />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 3 }}>
          <MuiSelect
            disabled={!editable}
            options={destinationOptions}
            placeholder={t("shipping_cm.destination")}
            value={destinationOptions.find(opt => opt.value === String(shipment.destination)) || null}
            onChange={opt => onChange("destination", opt?.value ?? null)}
          />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 6 }}>
          <MuiTextField disabled={!editable} value={shipment.shipmentIdLogistica} onChange={v => onChange("shipmentIdLogistica", v)} label={t("shipping_cm.shipment_id_logistica")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 6 }}>
          <MuiTextField disabled={!editable} value={shipment.trackingNumber} onChange={v => onChange("trackingNumber", v)} label={t("shipping_cm.tracking_number")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 3 }}>
          <MuiTextField disabled={!editable} value={shipment.aLAttentionDe} onChange={v => onChange("aLAttentionDe", v)} label={t("shipping_cm.a_l_attention_de")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 3 }}>
          <MuiDate disabled={!editable} value={shipment.date} onChange={v => onChange("date", v)} label={t("shipping_cm.date")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 3 }}>
          <MuiDate disabled={!editable} value={shipment.etd} onChange={v => onChange("etd", v)} label={t("shipping_cm.etd")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 3 }}>
          <MuiDate disabled={!editable} value={shipment.eta} onChange={v => onChange("eta", v)} label={t("shipping_cm.eta")} />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 6 }}>
          <MuiSelect
            disabled={!editable}
            options={subCategoryOptions}
            placeholder={t("shipping_cm.sub_category")}
            value={subCategoryOptions.find(opt => opt.value === shipment.subCategory) || null}
            onChange={opt => onChange("subCategory", opt?.value ?? null)}
          />
        </MuiCol>
        <MuiCol size={{ xs: 12, md: 6 }}>
          <MuiSelect
            disabled={!editable}
            options={expeditionTypeOptions}
            placeholder={t("shipping_cm.transport_mode")}
            value={expeditionTypeOptions.find(opt => opt.value === shipment.transportMode) || null}
            onChange={opt => onChange("transportMode", opt?.value ?? null)}
          />
        </MuiCol>
      </MuiRow>
      <Divider sx={{ mt: 2 }} />
    </MuiBox>
  );
}

/**
 * One aggregated PO + SKU row: pallet/camera counters, CI number, and the
 * two editable cost totals (Frais de douane / Frais de transport).
 *
 * costDraft holds the in-progress (uncommitted) values while the user is
 * typing in either cost field; onCostCommit fires on blur and writes the
 * proportional split back to every underlying container.
 */
function POLine({ line, costDraft, onCiChange, onCostChange, onCostCommit, editable, t }) {
  const douaneValue = costDraft?.douaneTotal ?? line.douaneTotal;
  const transportValue = costDraft?.transportTotal ?? line.transportTotal;

  return (
    <MuiRow spacing={1} sx={{ alignItems: "center", mb: 1 }}>
      <MuiCol size={{ xs: 12, md: 2 }}>
        <MuiTextField disabled value={line.modelName ?? ""} label={t("shipping_cm.model")} />
      </MuiCol>
      <MuiCol size={{ xs: 12, md: 1.5 }}>
        <MuiTextField disabled value={line.sku} label={t("shipping_cm.sku")} />
      </MuiCol>
      <MuiCol size={{ xs: 6, md: 1 }}>
        <MuiTextField disabled value={line.palletCount} label={t("shipping_cm.pallets")} />
      </MuiCol>
      <MuiCol size={{ xs: 6, md: 1 }}>
        <MuiTextField disabled value={line.totalCameras} label={t("shipping_cm.cameras")} />
      </MuiCol>
      <MuiCol size={{ xs: 12, md: 3.5 }}>
        <MuiTextField
          disabled={!editable}
          value={line.ciNumber}
          onChange={onCiChange}
          label={t("shipping_cm.ci_number")}
          error={!line.ciNumber || line.ciMismatch}
          helperText={line.ciMismatch ? t("shipping_cm.ci_mismatch_warning") : undefined}
        />
      </MuiCol>
      <MuiCol size={{ xs: 6, md: 1.5 }}>
        <MuiTextField
          disabled={!editable}
          type="number"
          value={douaneValue}
          onChange={value => onCostChange("douaneTotal", value)}
          onBlur={onCostCommit}
          label={t("shipping_cm.customs_cost")}
        />
      </MuiCol>
      <MuiCol size={{ xs: 6, md: 1.5 }}>
        <MuiTextField
          disabled={!editable}
          type="number"
          value={transportValue}
          onChange={value => onCostChange("transportTotal", value)}
          onBlur={onCostCommit}
          label={t("shipping_cm.shipping_cost")}
        />
      </MuiCol>
    </MuiRow>
  );
}

function POGroup({ poGroup, isLast, costDrafts, onCiChange, onCostChange, onCostCommit, editable, t }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        backgroundColor: "grey.100",
        borderRadius: 1,
        px: 2,
        py: 0.5,
        mb: 1
      }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {poGroup.poNumber || poGroup.poId}
        </Typography>
        <Box sx={{
          fontSize: 11,
          fontWeight: 500,
          backgroundColor: "grey.300",
          borderRadius: 1,
          px: 1,
          py: 0.25,
          color: "text.secondary"
        }}>
          {poGroup.lines.length} {t("shipping_cm.lines")}
        </Box>
      </Box>
      {poGroup.lines.map(line => (
        <POLine
          key={line.key}
          line={line}
          costDraft={costDrafts[line.key]}
          onCiChange={value => onCiChange(line, value)}
          onCostChange={(field, value) => onCostChange(line, field, value)}
          onCostCommit={() => onCostCommit(line)}
          editable={editable}
          t={t}
        />
      ))}
      {!isLast && <Divider sx={{ mt: 2 }} />}
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function Template(props) {
  const { t } = props;
  const appContext = useAppContext();
  const [scannedValue, setScannedValue] = useState();
  const [scannerError, setScannerError] = useState();
  const [pallets, dispatch] = useReducer(palletsReducer, []);
  const [masterpacks, masterpacksDispatch] = useReducer(masterpacksReducer, []);
  // Loose items are display-only for now — loaded from the shipment, carried
  // through on save, but not editable on this screen yet.
  const [looseItems, setLooseItems] = useState([]);
  const [shipment, setShipment] = useState({
    shipmentCode: "",
    status: "Draft",
    origineLocationId: null,
    destination: "",
    shipmentIdLogistica: "",
    aLAttentionDe: "",
    date: "",
    subCategory: null,
    trackingNumber: "",
    eta: "",
    etd: "",
    transportMode: null,
  });

  // In-progress edits for Frais de douane / Frais de transport, keyed by
  // `${poId}__${itemSku}`. Cleared once the value is committed on blur.
  const [costDrafts, setCostDrafts] = useState({});

  const [isShipmentListOpen, setIsShipmentListOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const expeditionTypeOptions = useMemo(() =>
    expeditionType.map(e => ({ label: e.name, value: e.id }))
  , []);

  const [destinations, setDestinations] = useState([]);
  const destinationsPromiseRef = useRef(null);

  useEffect(() => {
    destinationsPromiseRef.current = netsuiteApi.getShippingLocations()
      .then(result => {
        // A failed NetSuite call can still resolve with a 200 and an
        // unexpected body, which .catch() never sees. Without this guard the
        // state goes undefined and destinationOptions throws on the next
        // render, taking the whole screen down.
        const locs = Array.isArray(result?.data?.data) ? result.data.data : [];
        if (locs.length === 0) {
          console.warn("getShippingLocations(): no locations returned", result?.data);
        }
        setDestinations(locs);
        return locs;
      })
      .catch(err => {
        console.error("Failed to load shipping locations", err);
        return [];
      });
  }, []);

  const destinationOptions = useMemo(() =>
    destinations.map(d => ({ label: d.name, value: d.id }))
  , [destinations]);

  const [plants, setPlants] = useState([]);

  useEffect(() => {
    plantApi.getPlants()
      .then(result => setPlants(Array.isArray(result?.data) ? result.data : []))
      .catch(err => console.error("Failed to load plants", err));
  }, []);

  const originCmOptions = useMemo(() =>
    plants
      .filter(p => p.netsuiteCmId)
      .map(p => ({ label: p.name, value: p.netsuiteCmId }))
  , [plants]);

  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  useEffect(() => {
    netsuiteApi.getMseList("subCategory")
      .then(result => {
        const values = Array.isArray(result?.data?.values) ? result.data.values : [];
        const options = values.map(v => ({ label: v.name, value: v.id }));
        setSubCategoryOptions(options);
      })
      .catch(err => console.error("Failed to load subCategory list", err));
  }, []);

  // Camera models, fetched once, used only as a fallback name lookup for
  // lines whose stored modelName is missing — mainly older shipments saved
  // before modelName was written at scan time.
  const [cameraModels, setCameraModels] = useState([]);

  useEffect(() => {
    cameraModelApi.getCameraModels()
      .then(result => setCameraModels(Array.isArray(result?.data) ? result.data : []))
      .catch(err => console.error("Failed to load camera models", err));
  }, []);

  const nameBySku = useMemo(() =>
    Object.fromEntries(
      cameraModels.filter(cm => cm.productCode).map(cm => [cm.productCode, cm.name ?? null])
    )
  , [cameraModels]);

  // PO-first aggregation of pallets + masterpacks. See buildViewMap /
  // groupViewMapByPo above and the "MSE Shipment UI — New Display Format"
  // spec for the underlying logic.
  const viewMap = useMemo(() => buildViewMap(pallets, masterpacks, looseItems, nameBySku), [pallets, masterpacks, looseItems, nameBySku]);
  const poGroups = useMemo(() => groupViewMapByPo(viewMap), [viewMap]);

  const SHIPMENT_STEPS = [
    { key: "Draft", label: t("shipping_cm.status_draft") },
    { key: "Sent",  label: t("shipping_cm.status_sent_to_netsuite") },
  ];

  const stepConditions = {
    "Sent": (
      (pallets.length > 0 || masterpacks.length > 0 || looseItems.length > 0) &&
      !!shipment.destination &&
      !!shipment.trackingNumber &&
      !!shipment.eta &&
      !!shipment.etd &&
      !!shipment.transportMode &&
      [...pallets, ...masterpacks].every(entry =>
        entry.pos.every(po =>
          po.cameraModel.every(model => !!model.ciNumber)
        )
      )
    ),
  };

  const currentIndex = SHIPMENT_STEPS.findIndex(s => s.key === shipment.status);
  const nextStep = SHIPMENT_STEPS[currentIndex + 1];
  const canAdvance = nextStep ? !!stepConditions[nextStep.key] : false;
  const editable = shipment.status !== 'Sent';

  function handleShipmentChange(field, value) {
    setShipment(prev => ({ ...prev, [field]: value }));
  }

  // Editing the CI Number on an aggregated PO + SKU line writes that value
  // onto every underlying pallet/masterpack cameraModel entry for that
  // PO + SKU.
  function handleCiChange(line, value) {
    dispatch({ type: "SET_CI_FOR_PO_SKU", poId: line.poId, itemSku: line.sku, value });
    masterpacksDispatch({ type: "SET_CI_FOR_PO_SKU", poId: line.poId, itemSku: line.sku, value });
    setLooseItems(prev => applyCiToLooseItems(prev, line.poId, line.sku, value));
  }

  // Tracks the user's typed value locally until they leave the field.
  function handleCostChange(line, field, value) {
    setCostDrafts(prev => ({
      ...prev,
      [line.key]: {
        douaneTotal: line.douaneTotal,
        transportTotal: line.transportTotal,
        ...prev[line.key],
        [field]: value,
      }
    }));
  }

  // On blur: split the entered total across every underlying line (pallets,
  // masterpacks and loose items together) using splitWithRemainderToLargest,
  // so the shares sum back to exactly what the user typed instead of
  // drifting off by a cent or two, then clear the draft so the field
  // reflects the recomputed total on the next render.
  function handleCostCommit(line) {
    const draft = costDrafts[line.key];
    if (!draft) return;

    const douaneTotal = Number(draft.douaneTotal ?? line.douaneTotal) || 0;
    const transportTotal = Number(draft.transportTotal ?? line.transportTotal) || 0;

    // Collect every line (pallet, masterpack, loose) contributing to this
    // PO + SKU, each identified by the key its own reducer/helper uses to
    // write back to it.
    const entries = [];
    for (const p of pallets) {
      const model = p.pos.find(po => po.poId === line.poId)?.cameraModel.find(m => m.itemSku === line.sku);
      if (model) entries.push({ id: p.palletNumber, quantity: model.quantity });
    }
    for (const mp of masterpacks) {
      const model = mp.pos.find(po => po.poId === line.poId)?.cameraModel.find(m => m.itemSku === line.sku);
      if (model) entries.push({ id: mp.masterpackNumber, quantity: model.quantity });
    }
    for (const entry of looseItems) {
      const l = entry.poId === line.poId ? entry.lines.find(l => l.itemSku === line.sku) : null;
      if (l) entries.push({ id: entry.poId, quantity: l.quantity ?? 0 });
    }

    const douaneById = splitWithRemainderToLargest(entries, douaneTotal);
    const transportById = splitWithRemainderToLargest(entries, transportTotal);
    const amountsById = new Map(
      entries.map(e => [e.id, {
        douane: douaneById.get(e.id) ?? 0,
        transport: transportById.get(e.id) ?? 0,
      }])
    );

    dispatch({ type: "SET_EXACT_COSTS_FOR_PO_SKU", poId: line.poId, itemSku: line.sku, amountsById });
    masterpacksDispatch({ type: "SET_EXACT_COSTS_FOR_PO_SKU", poId: line.poId, itemSku: line.sku, amountsById });
    setLooseItems(prev => applyExactCostsToLooseItems(prev, line.poId, line.sku, amountsById));

    setCostDrafts(prev => {
      const next = { ...prev };
      delete next[line.key];
      return next;
    });
  }

  // Loads a shipment by code and populates the whole screen. Shared by the
  // scanner and by the shipment list dialog. Throws on failure — callers
  // decide how to surface the error.
  async function loadShipment(shipmentCode) {
    const [result] = await Promise.all([
      shipmentApi.getShipment(shipmentCode),
      destinationsPromiseRef.current,
    ]);
    const s = result.data;

    setShipment({
      shipmentCode:        s.shipmentCode,
      status:              s.status,
      origineLocationId:   s.origineLocationId ?? null,
      destination:         s.destination,
      shipmentIdLogistica: s.shipmentIdLogistica ?? "",
      aLAttentionDe:       s.aLAttentionDe ?? "",
      date:                s.date ?? "",
      subCategory:         s.subCategory ?? null,
      trackingNumber:      s.trackingNumber,
      eta:                 s.eta,
      etd:                 s.etd,
      transportMode:       s.transportMode,
    });

    const loadedPallets = s.pallets ?? [];
    const loadedMasterpacks = s.masterpacks ?? [];
    const loadedLooseItems = s.looseItems ?? [];

    // Show immediately using the poNumber values already stored on the
    // shipment — pallets and masterpacks both appear at once.
    dispatch({ type: "RESET_PALLETS", payload: loadedPallets });
    masterpacksDispatch({ type: "RESET_MASTERPACKS", payload: loadedMasterpacks });
    setLooseItems(loadedLooseItems);
    setCostDrafts({});

    // Resolve/refresh PO numbers in the background and patch them in
    // once ready, without blocking the initial display.
    enrichWithPoNumbers(loadedPallets).then(enriched => {
      enriched.forEach(pallet => {
        dispatch({
          type: "UPDATE_PO_NUMBERS",
          palletNumber: pallet.palletNumber,
          poNumberMap: Object.fromEntries(pallet.pos.map(po => [po.poId, po.poNumber]))
        });
      });
    })
    .catch(err => console.error("Failed to resolve PO numbers for pallets", err));

    enrichWithPoNumbers(loadedMasterpacks).then(enriched => {
      enriched.forEach(mp => {
        masterpacksDispatch({
          type: "UPDATE_PO_NUMBERS",
          masterpackNumber: mp.masterpackNumber,
          poNumberMap: Object.fromEntries(mp.pos.map(po => [po.poId, po.poNumber]))
        });
      });
    })
    .catch(err => console.error("Failed to resolve PO numbers for masterpacks", err));

    // Loose items carry poId/poNumber at the entry level rather than in a
    // pos[] array, so they get their own resolution pass. Same rule as
    // everywhere else: unresolved stays blank, never back-filled with poId.
    const loosePoIds = [...new Set(loadedLooseItems.map(entry => entry.poId).filter(Boolean))];
    if (loosePoIds.length > 0) {
      findPoNumbers(loosePoIds).then(poNumberMap => {
        setLooseItems(prev => prev.map(entry => ({
          ...entry,
          poNumber: poNumberMap[entry.poId] || entry.poNumber || ""
        })));
      })
      .catch(err => console.error("Failed to resolve PO numbers for loose items", err));
    }
  }

  function handleNew() {
    setShipment({
      shipmentCode: "",
      status: "Draft",
      origineLocationId: null,
      destination: "",
      shipmentIdLogistica: "",
      aLAttentionDe: "",
      date: "",
      subCategory: null,
      trackingNumber: "",
      eta: "",
      etd: "",
      transportMode: null,
    });
    dispatch({ type: "RESET_PALLETS", payload: [] });
    masterpacksDispatch({ type: "RESET_MASTERPACKS", payload: [] });
    setLooseItems([]);
    setCostDrafts({});
    setScannedValue("");
    setScannerError("");
  }

  function handleOpenShipmentList() {
    setIsShipmentListOpen(true);
  }

  function handleCloseShipmentList() {
    setIsShipmentListOpen(false);
  }

  // Picked a shipment in the dialog — close it and load that shipment.
  async function handleSelectShipment(shipmentCode) {
    setIsShipmentListOpen(false);
    try {
      await loadShipment(shipmentCode);
    } catch (err) {
      console.error(err);
      appContext.showMessage(t('error'), t("shipping_cm.shipment_load_error"));
    }
  }

  function scannerOnKeyDown(e) {
    if (e.key === 'Enter') {
      handleScan(e.target.value);
    }
  }

  async function handleScan(rawScan) {
    const scan = rawScan?.trim() ?? "";
    setScannerError("");

    if (!editable) {
      setScannedValue("");
      return;
    }

    if (/^PL-[A-Z0-9]+$/.test(scan)) {
      // ── Pallet ──────────────────────────────────────────────────────────────
      if (pallets.some(p => p.palletNumber === scan)) {
        setScannerError(t("shipping_cm.pallet_already_scanned"));
        setScannedValue("");
        return;
      }
      try {
        const result = await palletApi.getPallet(scan);
        const transformed = transformPalletData(result.data);
        dispatch({ type: "ADD_PALLET", payload: transformed });

        // Resolve PO numbers in background and update display
        enrichWithPoNumbers([transformed]).then(([enriched]) => {
          dispatch({ type: "UPDATE_PO_NUMBERS", palletNumber: transformed.palletNumber, poNumberMap: Object.fromEntries(enriched.pos.map(po => [po.poId, po.poNumber])) });
        })
        .catch(err => console.error("Failed to resolve PO numbers for scanned pallet", err));
      } catch (err) {
        setScannerError(t("shipping_cm.invalid_pallet_barcode"));
      }

    } else if (/^\d{10,16}$/.test(scan)) {
      // ── Masterpack ───────────────────────────────────────────────────────────
      if (masterpacks.some(mp => mp.masterpackNumber === scan)) {
        setScannerError(t("shipping_cm.pallet_already_scanned"));
        setScannedValue("");
        return;
      }
      try {
        const result = await masterpackApi.getMasterpackInfo(scan);
        const transformed = transformMasterpackData(result.data);
        masterpacksDispatch({ type: "ADD_MASTERPACK", payload: transformed });

        // Resolve PO numbers in background and update display
        enrichWithPoNumbers([transformed]).then(([enriched]) => {
          masterpacksDispatch({ type: "UPDATE_PO_NUMBERS", masterpackNumber: transformed.masterpackNumber, poNumberMap: Object.fromEntries(enriched.pos.map(po => [po.poId, po.poNumber])) });
        })
        .catch(err => console.error("Failed to resolve PO numbers for scanned masterpack", err));
      } catch (err) {
        console.error("masterpack fetch error:", err?.response?.status, err?.response?.data, err);
        setScannerError(t("shipping_cm.invalid_pallet_barcode"));
      }

    } else {
      // ── Shipment code ────────────────────────────────────────────────────────
      try {
        await loadShipment(scan);
      } catch (err) {
        setScannerError(t("shipping_cm.invalid_pallet_barcode"));
      }
    }

    setScannedValue("");
  }

  function buildPayload() {
    return {
      ...shipment,
      pallets,
      masterpacks,
      // Carried through untouched. Without this, loading a shipment that has
      // loose items and pressing Save would silently delete them.
      looseItems,
    };
  }

  // Saves the shipment, then asks the backend to send it. The backend owns
  // the status transition — it flips Draft → Sent only once NetSuite has
  // accepted the payload, so a failed send leaves the shipment editable and
  // retryable.
 async function handleNextStep() {
    if (!nextStep || submitting) return;
    setSubmitting(true);
    try {
      let code = shipment.shipmentCode;

      if (code) {
        await shipmentApi.updateShipment(code, buildPayload());
      } else {
        const created = await shipmentApi.createShipment(buildPayload());
        code = created.data.shipmentCode;
        setShipment(prev => ({ ...prev, shipmentCode: code }));
      }

      const sent = await shipmentApi.sendShipmentToNetsuite(code);
      const eventId = sent.data.eventId;

      setShipment(prev => ({ ...prev, status: nextStep.key }));
      appContext.showMessage(t('success'), t("shipping_cm.shipment_sent", { shipmentCode: code, eventId }));
    } catch (err) {
      console.error(err);
      appContext.showMessage(t('error'), t("shipping_cm.shipment_send_error"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSave() {
    try {
      if (shipment.shipmentCode) {
        await shipmentApi.updateShipment(shipment.shipmentCode, buildPayload());
        appContext.showMessage(t('success'), t("shipping_cm.shipment_saved", { shipmentCode: shipment.shipmentCode }));
      } else {
        const created = await shipmentApi.createShipment(buildPayload());
        const shipmentCode = created.data.shipmentCode;
        setShipment(prev => ({ ...prev, shipmentCode }));
        appContext.showMessage(t('success'), t("shipping_cm.shipment_saved", { shipmentCode }));
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage(t('error'), "An unexpected error happened.");
    }
  }

  return (
    <>
      <StandardPage
        title={t("shipping_cm.shipping_cm")}
        description={t("shipping_cm.title_desc")}
        showPrinterStatus={false}
        showPrinterRFIDStatus={false}
      >
        <StandardPageTopContent>
          <MuiRow spacing={1} sx={{ alignItems: "center" }}>
            <MuiCol size="auto">
              <MuiButton
                startIcon={<ListIcon />}
                onClick={handleOpenShipmentList}
              >
                {t("shipping_cm.list")}
              </MuiButton>
            </MuiCol>
            <MuiCol size="grow">
              <ScannerInput
                onChange={setScannedValue}
                onKeyDown={scannerOnKeyDown}
                value={scannedValue}
                label={t("shipping_cm.enter_shipment_id")}
                error={scannerError}
              />
            </MuiCol>
            <MuiCol size="auto">
              <MuiButton onClick={handleNew}>
                {t("shipping_cm.new")}
              </MuiButton>
            </MuiCol>
          </MuiRow>
        </StandardPageTopContent>
        <StandardPageMainContent>
          <MuiRow sx={{ mb: 3 }}>
            <MuiCol size={{ xs: 12, md: 10 }}>
              <MuiStepper steps={SHIPMENT_STEPS} activeStep={shipment.status} />
            </MuiCol>
            <MuiCol size={{ xs: 12, md: 2 }}>
              <MuiButton
                onClick={handleNextStep}
                disabled={!canAdvance || submitting}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {t("shipping_cm.send_to_netsuite")}
              </MuiButton>
            </MuiCol>
          </MuiRow>
          <ShipmentSection
            shipment={shipment}
            onChange={handleShipmentChange}
            expeditionTypeOptions={expeditionTypeOptions}
            originCmOptions={originCmOptions}
            destinationOptions={destinationOptions}
            subCategoryOptions={subCategoryOptions}
            editable={editable}
            t={t}
          />

          {poGroups.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {t("shipping_cm.purchase_orders")}
              </Typography>
              {poGroups.map((poGroup, gi) => (
                <POGroup
                  key={poGroup.poId}
                  poGroup={poGroup}
                  isLast={gi === poGroups.length - 1}
                  costDrafts={costDrafts}
                  onCiChange={handleCiChange}
                  onCostChange={handleCostChange}
                  onCostCommit={handleCostCommit}
                  editable={editable}
                  t={t}
                />
              ))}
            </>
          )}

          <MuiRow sx={{ mt: 2 }}>
            <MuiCol size={{ xs: 12, md: 2 }}>
              <MuiButton onClick={handleSave} disabled={!editable}>
                {t("shipping_cm.save")}
              </MuiButton>
            </MuiCol>
          </MuiRow>
        </StandardPageMainContent>
      </StandardPage>
      <ShipmentListDialog
        isOpen={isShipmentListOpen}
        plants={plants}
        destinations={destinations}
        onClose={handleCloseShipmentList}
        onSelect={handleSelectShipment}
      />
    </>
  );
}

Template.propTypes = {
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
  auth: state.auth,
  errors: state.errors
});

export default compose(
  withTranslation(),
  connect(mapStateToProps)
)(Template);